/**
 * Two-Factor Verification Page
 * 6-digit code entry after password login
 */

import React, { useState, useRef, useEffect } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/common/Button';
import * as authService from '@/services/auth.service';
import { AuthLayout } from './AuthLayout';
import { ShieldCheck } from 'lucide-react';

export const VerifyTwoFactorPage: React.FC = () => {
  const location = useLocation();
  const { verifyTwoFactor, isLoading, error, clearError } = useAuth();

  const twoFactorToken = location.state?.twoFactorToken as string | undefined;
  const email = location.state?.email as string | undefined;

  const [digits, setDigits] = useState<string[]>(['', '', '', '', '', '']);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendError, setResendError] = useState<string | null>(null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  // Focus first input on mount
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  // If no token in state, redirect to login
  if (!twoFactorToken || !email) {
    return <Navigate to="/login" replace />;
  }

  // Mask email for display
  const maskedEmail = (() => {
    const [local, domain] = email.split('@');
    if (local.length <= 2) return `${local}@${domain}`;
    return `${local[0]}${'*'.repeat(local.length - 2)}${local[local.length - 1]}@${domain}`;
  })();

  const handleDigitChange = (index: number, value: string) => {
    // Only allow single digit
    const digit = value.replace(/\D/g, '').slice(-1);

    const newDigits = [...digits];
    newDigits[index] = digit;
    setDigits(newDigits);

    if (error) clearError();

    // Auto-advance to next input
    if (digit && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all 6 digits entered
    if (digit && index === 5) {
      const code = newDigits.join('');
      if (code.length === 6) {
        verifyTwoFactor(twoFactorToken, code);
      }
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      // Move to previous input on backspace when current is empty
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;

    const newDigits = [...digits];
    for (let i = 0; i < 6; i++) {
      newDigits[i] = pasted[i] || '';
    }
    setDigits(newDigits);

    // Focus last filled input or submit
    if (pasted.length === 6) {
      inputRefs.current[5]?.focus();
      verifyTwoFactor(twoFactorToken, pasted);
    } else {
      inputRefs.current[Math.min(pasted.length, 5)]?.focus();
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const code = digits.join('');
    if (code.length === 6) {
      verifyTwoFactor(twoFactorToken, code);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setResendError(null);

    try {
      await authService.resendTwoFactor(twoFactorToken);
      setResendCooldown(60);
      // Clear digits
      setDigits(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } catch (err) {
      setResendError(err instanceof Error ? err.message : 'Грешка при изпращане');
    }
  };

  return (
    <AuthLayout>
      {/* Header */}
      <div className="text-center">
        <ShieldCheck className="mx-auto h-12 w-12 text-primary-400 mb-3" strokeWidth={1.5} />
        <h1 className="text-3xl font-bold bg-gradient-to-r from-primary-400 to-primary-600 bg-clip-text text-transparent">
          HighFive
        </h1>
        <h2 className="mt-3 text-lg font-semibold text-gray-300">
          Код за верификация
        </h2>
        <p className="mt-2 text-sm text-gray-400">
          Изпратихме 6-цифрен код на <strong className="text-gray-300">{maskedEmail}</strong>
        </p>
      </div>

      {/* Error Alert */}
      {(error || resendError) && (
        <div className="auth-error px-4 py-3 rounded-xl">
          <p className="text-sm">{error || resendError}</p>
        </div>
      )}

      {/* Code Input */}
      <form onSubmit={handleSubmit} className="mt-8 space-y-6">
        <div className="flex justify-center gap-3" onPaste={handlePaste}>
          {digits.map((digit, index) => (
            <input
              key={index}
              ref={el => { inputRefs.current[index] = el; }}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={1}
              value={digit}
              onChange={(e) => handleDigitChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              className="w-12 h-14 text-center text-2xl font-bold bg-white/[0.06] border-2 border-white/[0.1] rounded-xl text-white focus:border-primary-500/50 focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
              disabled={isLoading}
            />
          ))}
        </div>

        <Button
          type="submit"
          variant="primary"
          isLoading={isLoading}
          fullWidth
          disabled={digits.join('').length !== 6}
          className="py-3 rounded-xl font-semibold"
        >
          Потвърди
        </Button>
      </form>

      {/* Resend */}
      <div className="text-center space-y-2">
        <p className="text-sm text-gray-500">
          Не получихте код?
        </p>
        <button
          onClick={handleResend}
          disabled={resendCooldown > 0 || isLoading}
          className="text-sm font-medium text-primary-400 hover:text-primary-300 disabled:text-gray-600 disabled:cursor-not-allowed transition-colors"
        >
          {resendCooldown > 0
            ? `Изпрати отново (${resendCooldown}с)`
            : 'Изпрати отново'
          }
        </button>
      </div>

      {/* Back to login */}
      <div className="pt-4 border-t border-white/[0.05] text-center">
        <a
          href="/login"
          className="text-sm text-gray-500 hover:text-gray-400 transition-colors"
        >
          &larr; Обратно към вход
        </a>
      </div>
    </AuthLayout>
  );
};
